(function() {
  'use strict';

  const RescueFillTracker = {
    config: {
      apiUrl: window.rescueFill?.apiUrl || '/wp-json/rescue/v1',
      nonce: window.rescueFill?.nonce || '',
      saveInterval: window.rescueFill?.settings?.saveInterval || 5000,
      debounceDelay: 2000,
      enabled: window.rescueFill?.settings?.enabled || false,
      gdprMode: window.rescueFill?.settings?.gdprMode || false
    },

    tracked: new Set(),
    saveTimeout: null,
    currentFormData: {},
    recoveryData: null,

    init() {
      if (!this.config.enabled) return;

      this.detectForms();
      this.loadPersistedData();
      this.setupGlobalListeners();

      // Check for recovery data
      if (this.recoveryData) {
        this.prefillFromRecovery();
      }
    },

    detectForms() {
      // Contact Form 7
      const cf7Forms = document.querySelectorAll('.wpcf7-form');
      cf7Forms.forEach(form => this.monitorForm(form, 'contact-form-7'));

      // WPForms
      const wpForms = document.querySelectorAll('.wpforms-form');
      wpForms.forEach(form => this.monitorForm(form, 'wpforms'));

      // Gravity Forms
      const gfForms = document.querySelectorAll('.gform_wrapper form');
      gfForms.forEach(form => this.monitorForm(form, 'gravityforms'));

      // Ninja Forms
      const nfForms = document.querySelectorAll('.nf-form-cont form');
      nfForms.forEach(form => this.monitorForm(form, 'ninjaforms'));

      // Elementor Forms
      const elForms = document.querySelectorAll('.elementor-form');
      elForms.forEach(form => this.monitorForm(form, 'elementor'));

      // Generic forms with email fields
      const emailInputs = document.querySelectorAll('input[type="email"]');
      emailInputs.forEach(input => {
        const form = input.closest('form');
        if (form && !this.tracked.has(form)) {
          this.monitorForm(form, 'generic');
        }
      });
    },

    monitorForm(form, type) {
      if (this.tracked.has(form)) return;
      this.tracked.add(form);

      const emailField = form.querySelector('input[type="email"]');
      if (!emailField) return;

      const formId = this.getFormId(form, type);

      // Monitor all inputs for changes
      form.addEventListener('input', (e) => {
        // Skip sensitive fields
        if (this.isSensitiveField(e.target)) {
          return;
        }

        this.debouncedSave(form, formId, emailField);
      });

      // Monitor blur on email field specifically
      emailField.addEventListener('blur', () => {
        if (emailField.value.trim() && this.isValidEmail(emailField.value.trim())) {
          this.captureAndSave(form, formId, emailField);
        }
      });

      // Mark as submitted on form submit
      form.addEventListener('submit', () => {
        this.markAsSubmitted(formId, emailField.value);
      });

      // Handle CF7 AJAX submission
      if (type === 'contact-form-7') {
        document.addEventListener('wpcf7mailsent', (event) => {
          if (event.detail && event.detail.contactFormId) {
            this.markAsSubmitted(formId, emailField.value);
          }
        });
      }
    },

    getFormId(form, type) {
      if (type === 'contact-form-7') {
        const wrapper = form.closest('.wpcf7');
        return 'cf7-' + (wrapper?.getAttribute('data-id') || wrapper?.id || form.id || 'unknown');
      }
      if (type === 'wpforms') {
        return 'wpforms-' + (form.getAttribute('data-formid') || form.id || 'unknown');
      }
      if (type === 'gravityforms') {
        const idMatch = form.id?.match(/gform_(\d+)/);
        return 'gf-' + (idMatch ? idMatch[1] : 'unknown');
      }
      if (type === 'ninjaforms') {
        const nfWrapper = form.closest('.nf-form-cont');
        return 'nf-' + (nfWrapper?.id?.replace('nf-form-', '') || 'unknown');
      }
      if (type === 'elementor') {
        return 'elementor-' + (form.id || form.getAttribute('name') || 'unknown');
      }
      return form.id || form.className?.split(' ')[0] || 'form-' + Math.random().toString(36).substr(2, 9);
    },

    isSensitiveField(field) {
      const name = (field.name || '').toLowerCase();
      const type = (field.type || '').toLowerCase();
      const sensitivePatterns = ['password', 'passwd', 'pass', 'credit', 'card', 'cvv', 'cvc', 'ssn', 'secret', 'token'];

      if (type === 'password' || type === 'hidden') return true;

      return sensitivePatterns.some(pattern => name.includes(pattern));
    },

    debouncedSave(form, formId, emailField) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = setTimeout(() => {
        this.captureAndSave(form, formId, emailField);
      }, this.config.debounceDelay);
    },

    captureAndSave(form, formId, emailField) {
      const email = emailField.value.trim();

      if (!email || !this.isValidEmail(email)) return;

      const formData = new FormData(form);
      const data = {};

      formData.forEach((value, key) => {
        if (!this.isSensitiveKey(key)) {
          data[key] = value;
        }
      });

      this.saveToBackend({
        email,
        form_id: formId,
        form_name: this.getFormName(form),
        form_data: data,
        page_url: window.location.href
      });
    },

    isSensitiveKey(key) {
      const lower = key.toLowerCase();
      return ['password', 'passwd', 'credit', 'card', 'cvv', 'cvc', 'ssn', 'secret', 'token', '_wpnonce', '_wp_http_referer'].some(
        pattern => lower.includes(pattern)
      );
    },

    async saveToBackend(data) {
      try {
        const response = await fetch(this.config.apiUrl + '/track', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': this.config.nonce
          },
          body: JSON.stringify(data)
        });

        const result = await response.json();

        if (result.success && result.lead_id) {
          localStorage.setItem('RESCUE_lead_id', result.lead_id);
          this.currentFormData = data;
        }
      } catch (error) {
        // Silently fail - save to localStorage as fallback
        this.saveToStorage(data);
      }
    },

    async markAsSubmitted(formId, email) {
      if (!email || !this.isValidEmail(email)) return;

      try {
        await fetch(this.config.apiUrl + '/track', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': this.config.nonce
          },
          body: JSON.stringify({
            email,
            form_id: formId,
            action: 'submit'
          })
        });

        localStorage.removeItem('RESCUE_lead_id');
        localStorage.removeItem('RESCUE_form_data');
      } catch (error) {
        // Silent fail
      }
    },

    saveToStorage(data) {
      try {
        localStorage.setItem('RESCUE_form_data', JSON.stringify(data));
      } catch (e) {
        // Storage quota exceeded
      }
    },

    loadPersistedData() {
      try {
        const stored = localStorage.getItem('RESCUE_form_data');
        if (stored) {
          this.currentFormData = JSON.parse(stored);
        }
      } catch (e) {
        // Invalid JSON
      }
    },

    setupGlobalListeners() {
      // Re-detect forms on dynamic content load
      const observer = new MutationObserver((mutations) => {
        let formAdded = false;
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length) {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1 && (node.tagName === 'FORM' || node.querySelector?.('form'))) {
                formAdded = true;
              }
            });
          }
        });
        if (formAdded) {
          this.detectForms();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    },

    getFormName(form) {
      // Try various attributes for form name
      const title = form.getAttribute('data-form-title') ||
                    form.getAttribute('data-name') ||
                    form.getAttribute('aria-label');
      if (title) return title;

      // Look for a heading near the form
      const heading = form.closest('section')?.querySelector('h1, h2, h3, h4');
      if (heading) return heading.textContent.trim().substring(0, 100);

      return form.id || 'Unknown Form';
    },

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    /**
     * Pre-fill form fields from recovery data
     */
    prefillFromRecovery() {
      if (!this.recoveryData || !this.recoveryData.form_data) return;

      const formData = this.recoveryData.form_data;

      // Try to find the matching form
      const forms = document.querySelectorAll('form');
      forms.forEach((form) => {
        Object.entries(formData).forEach(([key, value]) => {
          const field = form.querySelector('[name="' + key + '"]');
          if (field && !this.isSensitiveField(field)) {
            if (field.type === 'checkbox' || field.type === 'radio') {
              field.checked = field.value === value;
            } else if (field.tagName === 'SELECT') {
              field.value = value;
            } else {
              field.value = value;
            }

            // Trigger change event for React/Vue/etc
            field.dispatchEvent(new Event('input', { bubbles: true }));
            field.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });

      // Clear recovery data
      this.recoveryData = null;
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => RescueFillTracker.init());
  } else {
    RescueFillTracker.init();
  }

  // Expose for recovery link usage
  window.RescueFillTracker = RescueFillTracker;
})();
